import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/feature/MainLayout";
import StatCard from "./components/StatCard";
import RevenueChart from "./components/RevenueChart";
import GisMiniMap from "./components/GisMiniMap";
import AlarmScroll from "./components/AlarmScroll";
import TodayRevenueChart from "./components/TodayRevenueChart";
import AIAlertPanel from "./components/AIAlertPanel";
import { dashboardStats, iotDeviceStats } from "@/mocks/dashboard";

const quickActions = [
  { label: "新增用户", icon: "ri-user-add-line", path: "/profile/resident" },
  { label: "实时抄表", icon: "ri-pulse-line", path: "/meter/realtime" },
  { label: "购气操作", icon: "ri-gas-station-line", path: "/sales/purchase" },
  { label: "安检管理", icon: "ri-shield-check-line", path: "/safety" },
  { label: "报装业务", icon: "ri-tools-line", path: "/install" },
  { label: "银行代扣", icon: "ri-bank-line", path: "/bank" },
  { label: "短信催缴", icon: "ri-message-2-line", path: "/sms" },
  { label: "数据大屏", icon: "ri-tv-2-line", path: "/screen" },
  { label: "GIS地图", icon: "ri-map-2-line", path: "/gis" },
  { label: "AI报告", icon: "ri-robot-2-line", path: "/report/ai" },
  { label: "费用管理", icon: "ri-money-cny-circle-line", path: "/fee/query" },
  { label: "系统设置", icon: "ri-settings-3-line", path: "/settings" },
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            title="总用户数"
            value={dashboardStats.totalUsers.toLocaleString()}
            subValue={`居民 ${dashboardStats.residentUsers.toLocaleString()} / 工商 ${dashboardStats.commercialUsers.toLocaleString()}`}
            icon="ri-group-line"
            iconBg="bg-orange-50"
            iconColor="text-[#E8531A]"
            trend={dashboardStats.revenueGrowth}
            trendLabel="较上月"
          />
          <StatCard
            title="本月营收"
            value={`¥${(dashboardStats.monthlyRevenue / 10000).toFixed(2)}万`}
            subValue="较上月 +12.5%"
            icon="ri-money-cny-circle-line"
            iconBg="bg-green-50"
            iconColor="text-green-600"
            trend={12.5}
            trendLabel="较上月"
          />
          <StatCard
            title="本月用气量"
            value={`${(dashboardStats.monthlyGasVolume / 10000).toFixed(2)}万m³`}
            subValue="较上月 +8.3%"
            icon="ri-fire-line"
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            trend={8.3}
            trendLabel="较上月"
          />
          <StatCard
            title="活跃设备"
            value={dashboardStats.activeDevices.toLocaleString()}
            subValue={`告警 ${dashboardStats.alarmCount} / 待检 ${dashboardStats.pendingInspection}`}
            icon="ri-cpu-line"
            iconBg="bg-teal-50"
            iconColor="text-teal-600"
          />
        </div>

        {/* IoT Device Status */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "在线设备", value: iotDeviceStats.online.toLocaleString(), color: "text-green-600", bg: "bg-green-50", dot: "bg-green-500" },
            { label: "离线设备", value: iotDeviceStats.offline.toLocaleString(), color: "text-gray-500", bg: "bg-gray-50", dot: "bg-gray-400" },
            { label: "告警设备", value: iotDeviceStats.alarm.toLocaleString(), color: "text-red-600", bg: "bg-red-50", dot: "bg-red-500" },
            { label: "维护中", value: iotDeviceStats.maintenance.toLocaleString(), color: "text-amber-600", bg: "bg-amber-50", dot: "bg-amber-500" },
          ].map((item, i) => (
            <div key={i} className={`${item.bg} rounded-xl p-3 flex items-center gap-3`}>
              <div className={`w-2 h-2 rounded-full ${item.dot} flex-shrink-0`}></div>
              <div>
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className={`text-lg font-bold ${item.color}`}>{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* AI Alert Panel + Today Todos */}
        <AIAlertPanel />

        {/* Main content grid */}
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <RevenueChart />
          </div>
          <div>
            <TodayRevenueChart />
          </div>
        </div>

        {/* GIS + Alarm + Quick Actions */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <GisMiniMap />
          </div>
          <div>
            <AlarmScroll />
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">快捷操作</h3>
            <div className="grid grid-cols-3 gap-2">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => navigate(action.path)}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer group"
                >
                  <div className="w-9 h-9 rounded-xl bg-gray-100 group-hover:bg-[#E8531A] flex items-center justify-center transition-colors">
                    <i className={`${action.icon} text-gray-500 group-hover:text-white text-base transition-colors`}></i>
                  </div>
                  <span className="text-[10px] text-gray-600 group-hover:text-[#E8531A] transition-colors whitespace-nowrap">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
